import { Button, DataTable, FilterableDataTable } from "@ventumsoftware/react-toolkit";
import { ContentContainer } from "../../components/content/ContentContainer";
import { ForbiddenPage } from "../ForbiddenPage";
import { Loading } from "../../components/Loading";
import { PageHeader } from "../../components/content/PageHeader";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { type FC, useEffect } from "react";
import { useApi, useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import { useModal } from "../../components/layout/ModalProvider";
import { AddChecklistModal } from "../../modals/AddChecklistModal";
import { useChecklistPermissions } from "../../permissions/checklistPolicy";
import { useNavigate } from "react-router";

// Checklists are fetched ad hoc here (via useFetchedResource, same primitive APIContext uses
// internally) rather than added to the global APIContext index — this is a new, admin-focused
// resource that isn't needed app-wide the way companies/question sets are. Permissions themselves
// (`useChecklistPermissions`) ARE global — see src/permissions/.
export const SettingsChecklistsPage: FC = () => {
  const navigate = useNavigate();
  const { companies } = useApi();
  const { listChecklists } = useApiCall();
  const checklists = useFetchedResource(() => listChecklists());
  const { loading: permissionsLoading, canAccessChecklistSettings } = useChecklistPermissions();

  // Note: useApiCall()'s methods aren't referentially stable across renders, so — matching
  // ExamDetailsPage's pattern — only stable/primitive values belong in this dependency array, not
  // `listChecklists`/`checklists.reload` itself (which would refire every render).
  useEffect(() => {
    checklists.reload(undefined);
  }, []);

  const addChecklistModal = useModal({
    title: "Nieuwe checklist",
    body: <AddChecklistModal onCreated={(checklist) => navigate(`/settings/checklists/${checklist.uuid}`)} />,
  });

  const companyName = (companyUuid: string | null) => {
    if (companyUuid === null) return "Globaal";
    return companies.find((c) => c.id === companyUuid)?.name ?? companyUuid;
  };

  return (
    <Loading loading={permissionsLoading}>
      {!canAccessChecklistSettings() ? (
        <ForbiddenPage />
      ) : (
        <ContentContainer size={"md"}>
          <div className={"flex items-center justify-between"}>
            <PageHeader>Checklisten</PageHeader>
            <Button
              type={"primary"}
              size={"md"}
              text={"Nieuwe checklist"}
              icon={faPlus}
              onClick={() => addChecklistModal.open()}
            />
          </div>

          <Loading loading={checklists.loading}>
            <FilterableDataTable
              keyProperty={"uuid"}
              data={checklists.resource ?? []}
              onClickRow={(checklist) => navigate(`/settings/checklists/${checklist.uuid}`)}
              columns={[
                { header: "Materiaaltype", property: "materialType" },
                { header: "Hijscategorie", property: "liftingCategoryLabel" },
                {
                  header: "Bedrijf",
                  property: "companyUuid",
                  transform: (value) => companyName(value),
                },
                { header: "Aantal kaarten", property: "cards", transform: (value) => value.length },
              ]}
            />
          </Loading>
        </ContentContainer>
      )}
    </Loading>
  );
};
